import {
  useAuth,
  useOrganization,
  useOrganizationList,
} from '@clerk/clerk-react';
import { Building2, CreditCard, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useGetMySubscription } from '@/hooks/subscription/useGetMySubscription';
import { getFallbackURL } from '@/lib/route';
import { hasTierAccess, SubscriptionTier } from '@/types/subscription.type';

const TOKEN_TEMPLATE = 'ff-backend-new' as const;
const TOKEN_STORAGE_KEY = 'access-token' as const;

interface DashboardGuardProps {
  children: React.ReactNode;
}

export const DashboardGuard = ({ children }: DashboardGuardProps) => {
  const { isLoaded, isSignedIn, signOut, getToken } = useAuth();
  const { organization } = useOrganization();
  const { userMemberships, setActive } = useOrganizationList({
    userMemberships: { infinite: true, keepPreviousData: true },
  });
  const [tokenReady, setTokenReady] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(false);

  // derived values
  const hasNoAccess = !isSignedIn;
  const isAuthLoading = !isLoaded;
  const isOrgsLoading = userMemberships.isLoading;
  const hasNoMembership = !isOrgsLoading && userMemberships.data?.length === 0;

  const zeeMemberships = userMemberships.data?.filter(
    (m) => m.role === 'org:zee'
  );
  const hasWrongRole =
    !isOrgsLoading && (!zeeMemberships || zeeMemberships.length === 0);

  useEffect(() => {
    if (!organization) return;

    setTokenReady(false);
    getToken({ template: TOKEN_TEMPLATE }).then((token) => {
      if (token) {
        window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
        setTokenReady(true);
      }
    });
  }, [organization?.id, getToken]);

  const isReadyForSubscriptionCheck =
    isLoaded &&
    isSignedIn &&
    !hasNoMembership &&
    !hasWrongRole &&
    !!organization &&
    tokenReady;

  const { result: subscriptionResult, isLoading: isSubscriptionLoading } =
    useGetMySubscription(isReadyForSubscriptionCheck);

  const subscription = subscriptionResult?.data;
  const isTrialing = subscription?.subscription.status === 'trialing';
  const isActive = subscription?.subscription.status === 'active';
  const isBypassPaywall = true;

  // TODO: uncomment this when we have a valid subscription
  const hasValidSubscription =
    isBypassPaywall ||
    (subscription &&
      (isTrialing ||
        (isActive &&
          hasTierAccess(subscription.plan.name, SubscriptionTier.EMERGING))));

  const handleSignOut = () => {
    signOut();
  };

  const handleSelectOrg = async (orgId: string) => {
    if (!setActive) return;
    setSwitchingOrg(true);
    await setActive({ organization: orgId });
    setSwitchingOrg(false);
  };

  if (isAuthLoading || isOrgsLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (hasNoAccess) {
    return <Navigate to={getFallbackURL()} replace />;
  }

  if (hasNoMembership) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50">
        <div className="mx-4 w-full max-w-md">
          <div className="rounded-lg bg-white p-8 text-center shadow-lg">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <Building2 className="size-8 text-blue-600" />
            </div>
            <h2 className="mb-2 text-xl font-semibold text-gray-900">
              No Organization Membership
            </h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You haven't joined any organizations yet. To use the Zee
              dashboard, you need to be a member of a Franchise Frameworks
              organization.
            </p>
            <Button onClick={handleSignOut}>Sign Out</Button>
          </div>
        </div>
      </div>
    );
  }

  if (hasWrongRole) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50">
        <div className="mx-4 w-full max-w-md">
          <div className="rounded-lg bg-white p-8 text-center shadow-lg">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <svg
                className="size-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="mb-2 text-xl font-semibold text-gray-900">
              Access Restricted
            </h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You are currently assigned to Franchise Frameworks with a
              different role. This dashboard is exclusively for franchise
              candidates (Zee role).
            </p>
            <Button onClick={handleSignOut}>Sign Out</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <OrgSelectorScreen
        memberships={zeeMemberships ?? []}
        onSelect={handleSelectOrg}
        onSignOut={handleSignOut}
        loading={switchingOrg}
      />
    );
  }

  if (!tokenReady || isSubscriptionLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (!hasValidSubscription) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50">
        <div className="mx-4 w-full max-w-md">
          <div className="rounded-lg bg-white p-8 shadow-lg">
            <div className="mb-4 flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                <CreditCard className="size-8 text-amber-600" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-900">
                Subscription Required
              </h2>
              <p className="leading-relaxed text-gray-600">
                Access to the Zee dashboard requires an{' '}
                <strong>Emerging plan</strong> or higher. Please ask your
                franchisor to upgrade their subscription.
              </p>
            </div>

            {(zeeMemberships?.length ?? 0) > 1 && (
              <div className="mt-6">
                <p className="mb-3 text-center text-sm font-medium text-gray-700">
                  Try switching to a different organization:
                </p>
                <OrgSelectorList
                  memberships={zeeMemberships ?? []}
                  currentOrgId={organization.id}
                  onSelect={handleSelectOrg}
                  loading={switchingOrg}
                />
              </div>
            )}

            <Button
              variant="outline"
              className="mt-4 w-full"
              onClick={handleSignOut}
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

type Membership = {
  organization: { id: string; name: string; imageUrl?: string };
};

interface OrgSelectorScreenProps {
  memberships: Membership[];
  onSelect: (orgId: string) => void;
  onSignOut: () => void;
  loading: boolean;
}

const OrgSelectorScreen = ({
  memberships,
  onSelect,
  onSignOut,
  loading,
}: OrgSelectorScreenProps) => (
  <div className="flex h-screen w-full items-center justify-center bg-gray-50">
    <div className="mx-4 w-full max-w-md">
      <div className="rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <Building2 className="size-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Select Organization
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Choose the franchise you want to access
          </p>
        </div>

        <OrgSelectorList
          memberships={memberships}
          onSelect={onSelect}
          loading={loading}
        />

        <Button variant="outline" className="mt-4 w-full" onClick={onSignOut}>
          Sign Out
        </Button>
      </div>
    </div>
  </div>
);

interface OrgSelectorListProps {
  memberships: Membership[];
  currentOrgId?: string;
  onSelect: (orgId: string) => void;
  loading: boolean;
}

const CurrentOrgLabel = ({ isCurrentOrg }: { isCurrentOrg: boolean }) => (
  <div className="mr-2 flex items-center gap-4">
    <div className="flex size-5 items-center justify-center text-xs font-semibold text-green-600">
      {isCurrentOrg ? 'current' : ''}
    </div>
  </div>
);

const OrgSelectorList = ({
  memberships,
  currentOrgId,
  onSelect,
  loading,
}: OrgSelectorListProps) => (
  <div className="space-y-2">
    {memberships.map((m) => {
      const isCurrent = m.organization.id === currentOrgId;
      return (
        <button
          key={m.organization.id}
          onClick={() => onSelect(m.organization.id)}
          disabled={loading || isCurrent}
          className="flex w-full items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {m.organization.imageUrl ? (
            <img
              src={m.organization.imageUrl}
              alt={m.organization.name}
              className="size-9 rounded-full object-cover"
            />
          ) : (
            <div className="flex size-9 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600">
              {m.organization.name.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="flex-1 text-sm font-medium text-gray-900">
            {m.organization.name}
          </span>
          <CurrentOrgLabel isCurrentOrg={isCurrent} />
          {loading && isCurrent && (
            <Loader2 className="size-4 animate-spin text-blue-500" />
          )}
        </button>
      );
    })}
  </div>
);
